import { cn } from "@/utils/cn";
import type { ReportResult, ReportTone } from "./types";

interface ReportPreviewProps {
  result: ReportResult;
  filterLines: { label: string; value: string }[];
}

const totalTones: Record<ReportTone, string> = {
  primary: "bg-primary-50 text-primary-600",
  accent: "bg-accent-50 text-accent-700",
  secondary: "bg-secondary-50 text-secondary-600",
  neutral: "bg-background-100 text-foreground-600",
};

export default function ReportPreview({ result, filterLines }: ReportPreviewProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div>
          <h3 className="font-heading text-base font-semibold text-foreground-950">{result.title}</h3>
          <p className="mt-0.5 text-xs text-foreground-500">{result.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterLines.map((line) => (
            <span
              key={line.label}
              className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-700"
            >
              <span className="font-label font-medium text-foreground-500">{line.label} :</span>
              {line.value}
            </span>
          ))}
        </div>
      </div>

      {result.totals.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {result.totals.map((total) => (
            <div key={total.label} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <span
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-lg",
                  totalTones[total.tone ?? "neutral"]
                )}
              >
                <i className="ri-bar-chart-2-line text-base" aria-hidden="true" />
              </span>
              <p className="mt-3 font-heading text-xl font-semibold leading-none text-foreground-950">
                {total.value}
              </p>
              <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">{total.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between gap-2 border-b border-background-200 px-4 py-3 md:px-5">
          <h4 className="font-heading text-sm font-semibold text-foreground-950">Détail ligne par ligne</h4>
          <span className="text-xs text-foreground-500">
            {result.rows.length} ligne{result.rows.length > 1 ? "s" : ""}
          </span>
        </div>

        {result.rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-inbox-line text-xl" aria-hidden="true" />
            </span>
            <p className="mt-3 text-sm font-label font-medium text-foreground-900">Aucune donnée pour ces filtres</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-foreground-500">
              Ajustez la période ou les filtres sélectionnés, puis relancez la génération du rapport.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60 text-left">
                  {result.columns.map((column) => (
                    <th
                      key={column.key}
                      className={cn(
                        "whitespace-nowrap px-4 py-3 text-xs font-label font-semibold uppercase tracking-wide text-foreground-600",
                        column.align === "right" && "text-right"
                      )}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, rowIndex) => (
                  <tr
                    key={`row-${rowIndex}`}
                    className="border-b border-background-100 last:border-0 hover:bg-background-100/50"
                  >
                    {result.columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          "px-4 py-3 align-top text-foreground-800",
                          column.align === "right" && "text-right font-label tabular-nums"
                        )}
                      >
                        {row[column.key] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result.note && (
          <p className="border-t border-background-200 px-4 py-3 text-xs text-foreground-500 md:px-5">{result.note}</p>
        )}
      </div>
    </div>
  );
}